import cn from 'classnames';
import Link from 'next/link';
import LinkButton from '@src/components/seo/linkButton';
import { PROPOSAL_HUBS } from '@src/lib/seo/hubs';

/**
 * Shell primitives for the server-rendered (SEO) pages.
 *
 * These pages have no styling requirements of their own: what search engines
 * read is the server-rendered markup, the real `<a href>`s, the heading
 * hierarchy and the text — none of which is affected by which colours or type
 * scale the page uses. So they use the same tokens as the rest of the app
 * (`body14`/`label12`/`h36-responsive`, `dark-neutral-*`, `semantic-*`) rather
 * than a parallel set, and the same container as the interactive pages so a
 * link from one to the other does not visibly change products.
 */

/** Matches the frame used by `governanceActionsClient` and the profile pages. */
export function PageShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className="flex w-full flex-col items-center bg-white dark:bg-dark-neutral-950">
      <div
        className={cn(
          'w-full max-w-[1440px] px-4 py-6 md:px-[72px] md:py-12 lg:px-[156px]',
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  intro,
  meta,
  children,
}: {
  title: string;
  intro?: string;
  meta?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <header className="flex flex-col gap-3">
      <h1 className="h36-responsive font-bold text-gray-900 dark:text-white">
        {title}
      </h1>
      {/*
        No reading measure on these. They head a list that spans the container,
        and a 68ch cap left the intro filling about half the page while the
        search box, the filters and the grid beneath it all ran full width.
        `ArticleColumn` still applies one where the text is the content.
      */}
      {intro && (
        <p className="body16 font-medium text-gray-600 dark:text-gray-400">
          {intro}
        </p>
      )}
      {meta && (
        <p className="body14 font-medium text-gray-500 dark:text-gray-500">
          {meta}
        </p>
      )}
      {children}
    </header>
  );
}

/**
 * Reading column for article-style pages.
 *
 * Centred in the shell with a reading measure: an article is the content, not
 * a header over a full-width list, so it gets a column rather than the
 * container width. Left-aligned it stranded ~470px of empty space down the
 * right of every guide.
 *
 * The measure lives here and nowhere else — the elements inside used to carry
 * their own `max-w-[68ch]` while the rules and section borders ran the full
 * container, which is what made these pages look half-drawn.
 */
export function ArticleColumn({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-[76ch] flex-col gap-4">
      {children}
    </div>
  );
}

export function SectionHeading({
  id,
  title,
  subtitle,
  as: Tag = 'h2',
}: {
  id?: string;
  title: string;
  subtitle?: string;
  as?: 'h2' | 'h3';
}) {
  return (
    <>
      <Tag id={id} className="h24 font-bold text-gray-900 dark:text-white">
        {title}
      </Tag>
      {subtitle && (
        <p className="body14 mt-2 font-medium text-gray-500 dark:text-gray-400">
          {subtitle}
        </p>
      )}
    </>
  );
}

/** The `Button` atom's neutral/xsmall look, as a real anchor a crawler follows. */
export function ChipLink({
  href,
  children,
  active = false,
}: {
  href: string;
  children: React.ReactNode;
  active?: boolean;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'body14 flex h-9 items-center gap-2 rounded-xl border-[1.5px] px-3 font-medium transition-colors',
        active
          ? 'border-brand bg-brand text-white'
          : 'border-gray-200 bg-white text-gray-900 hover:border-gray-900 hover:bg-gray-50 dark:border-dark-neutral-700 dark:bg-dark-neutral-800 dark:text-white dark:hover:border-white dark:hover:bg-dark-neutral-600'
      )}
    >
      {children}
    </Link>
  );
}

/**
 * Status and type are independent, so both can apply at once: a type hub takes
 * `?status=`, and every combination has exactly one URL.
 *
 * Type wins the path segment when both are set, which keeps that URL stable
 * whichever chip you clicked to reach it. `?status=` combinations are
 * `noindex`, canonical to the type hub — they are the same proposals sliced
 * again, and they exist for the reader, not for the index.
 */
export function hubHref(typeSlug?: string, statusSlug?: string): string {
  if (typeSlug) {
    return statusSlug
      ? `/governance_actions/browse/${typeSlug}?status=${statusSlug}`
      : `/governance_actions/browse/${typeSlug}`;
  }
  if (statusSlug) return `/governance_actions/browse/${statusSlug}`;
  return '/governance_actions';
}

/**
 * Filters for the governance corpus, identical on the explorer and every hub.
 *
 * Two rows, because status and type answer different questions: a flat row
 * mixing "Expired" with "Treasury withdrawal" asks people to pick from both at
 * once. Selecting in one row keeps the other, so the pair of rows means what it
 * looks like it means.
 *
 * This replaced the explorer's Filter dropdown, whose entire contents was the
 * type list repeated.
 */
export function HubChips({
  typeSlug,
  statusSlug,
}: {
  typeSlug?: string;
  statusSlug?: string;
}) {
  const row = (
    kind: 'state' | 'type',
    legend: string,
    anyLabel: string,
    selected?: string
  ) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="label12 w-14 shrink-0 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500">
        {legend}
      </span>
      <ChipLink
        href={
          kind === 'type'
            ? hubHref(undefined, statusSlug)
            : hubHref(typeSlug, undefined)
        }
        active={!selected}
      >
        {anyLabel}
      </ChipLink>
      {PROPOSAL_HUBS.filter((hub) => hub.kind === kind).map((hub) => (
        <ChipLink
          key={hub.slug}
          href={
            kind === 'type'
              ? hubHref(hub.slug, statusSlug)
              : hubHref(typeSlug, hub.slug)
          }
          active={hub.slug === selected}
        >
          {hub.label}
        </ChipLink>
      ))}
    </div>
  );

  return (
    <nav
      aria-label="Governance action categories"
      className="flex flex-col gap-2"
    >
      {row('state', 'Status', 'Any status', statusSlug)}
      {row('type', 'Type', 'Any type', typeSlug)}
    </nav>
  );
}

/**
 * The row directly above a list: what you are looking at on the left, controls
 * that act on it on the right.
 *
 * The count used to sit in the page header, above the search box and the
 * filters — describing a list three elements away, before the reader had even
 * chosen one.
 */
export function ListToolbar({
  summary,
  children,
}: {
  summary: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-dark-neutral-700">
      <p className="body14 font-medium text-gray-600 dark:text-gray-400">
        {summary}
      </p>
      {children}
    </div>
  );
}

/** The card surface used by `ContentCard` consumers and the profile tabs. */
export function Panel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 p-4 dark:border-dark-neutral-700 dark:bg-dark-neutral-900',
        className
      )}
    >
      {children}
    </div>
  );
}

export function Breadcrumb({
  items,
}: {
  items: { label: string; href: string }[];
}) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="label12 font-medium text-gray-500 dark:text-gray-500"
    >
      {items.map((item, index) => (
        <span key={item.href}>
          {index > 0 && <span className="mx-1.5">/</span>}
          <Link
            href={item.href}
            className="hover:text-gray-900 dark:hover:text-white"
          >
            {item.label}
          </Link>
        </span>
      ))}
    </nav>
  );
}

/** Divider matching the one the profile and tab surfaces use. */
export const DIVIDER = 'border-gray-200 dark:border-dark-neutral-700';

/**
 * The `Pagination` atom's bar with anchors instead of callbacks — pages past
 * the first have to be reachable without JavaScript for anything on them to be
 * indexed.
 *
 * The number row is windowed. Linking every page (167 of them on `/dreps`)
 * filled a screen with chips, and a crawler reaches the tail through `Next`
 * either way.
 */
export function PaginationLinks({
  page,
  totalPages,
  hrefForPage,
}: {
  page: number;
  totalPages: number;
  hrefForPage: (page: number) => string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 bg-gray-50 p-3 dark:border-dark-neutral-700 dark:bg-dark-neutral-800">
      <p className="body14 font-medium dark:text-white">
        Page {page} of {totalPages}
      </p>
      {totalPages > 1 && (
        <div className="hidden flex-wrap items-center gap-1 md:flex">
          {pageWindow(page, totalPages).map((entry, index) =>
            entry === null ? (
              <span
                key={`gap-${index}`}
                className="body14 px-1 text-gray-500 dark:text-gray-500"
              >
                …
              </span>
            ) : (
              <Link
                key={entry}
                href={hrefForPage(entry)}
                aria-current={entry === page ? 'page' : undefined}
                className={cn(
                  'body14 flex h-9 min-w-[36px] items-center justify-center rounded-lg px-2 font-medium transition-colors',
                  entry === page
                    ? 'bg-brand text-white'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-neutral-600'
                )}
              >
                {entry}
              </Link>
            )
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
        <LinkButton
          href={hrefForPage(page - 1)}
          buttonSize="small"
          disabled={page <= 1}
        >
          Previous
        </LinkButton>
        <LinkButton
          href={hrefForPage(page + 1)}
          buttonSize="small"
          disabled={page >= totalPages}
        >
          Next
        </LinkButton>
      </div>
    </div>
  );
}

/** First, last, and the pages either side of the current one; `null` is a gap. */
function pageWindow(page: number, totalPages: number): (number | null)[] {
  const wanted = [1, totalPages, page, page - 1, page + 1];
  if (page <= 3) wanted.push(2, 3, 4);
  if (page >= totalPages - 2)
    wanted.push(totalPages - 3, totalPages - 2, totalPages - 1);

  const numbers = wanted
    .filter(
      (n, index) => n >= 1 && n <= totalPages && wanted.indexOf(n) === index
    )
    .sort((a, b) => a - b);

  return numbers.flatMap((n, index) =>
    index > 0 && n - numbers[index - 1] > 1 ? [null, n] : [n]
  );
}
